import { ReactNode, useMemo, useRef } from 'react';
import { Pill } from '../Pill/Pill';
import { Tooltip } from '../Tooltip/Tooltip';

export interface StatusPillProps {
    value: string;
    customColor?: string;
    onlyContent?: boolean;
    fallback?: ReactNode;
    customLabel?: string;
    tooltip?: string;
}

export enum StatusPillValue {
    // Blue
    VERY_UNLIKELY = 'VERY_UNLIKELY',

    // Green
    LOW = 'LOW',
    MINOR = 'MINOR',
    NEGLIGIBLE = 'NEGLIGIBLE',
    UNLIKELY = 'UNLIKELY',

    // Yellow
    MODERATE = 'MODERATE',
    MEDIUM = 'MEDIUM',
    POSSIBLE = 'POSSIBLE',

    // Orange
    CRITICAL = 'CRITICAL',
    SIGNIFICANT = 'SIGNIFICANT',
    LIKELY = 'LIKELY',

    // Red
    DISASTROUS = 'DISASTROUS',
    HIGH = 'HIGH',
    MAJOR = 'MAJOR',
    VERY_HIGH = 'VERY_HIGH',
    VERY_LIKELY = 'VERY_LIKELY',
}

export function getStatusPillValue(value: string): StatusPillValue | undefined {
    const normalizedValue = value.replace(/\s+/g, '_').toLowerCase();
    const statusPillValues = Object.values(StatusPillValue);
    return statusPillValues.find(
        (statusPillValue) => statusPillValue.toLowerCase() === normalizedValue,
    );
}

export function getStatusPillColor(
    value: StatusPillValue | null | undefined,
): string {
    switch (value) {
        case StatusPillValue.VERY_LIKELY:
            return '#1697F1';
        case StatusPillValue.LOW:
        case StatusPillValue.MINOR:
        case StatusPillValue.NEGLIGIBLE:
        case StatusPillValue.UNLIKELY:
            return '#92CF55';
        case StatusPillValue.MODERATE:
        case StatusPillValue.MEDIUM:
        case StatusPillValue.POSSIBLE:
            return '#FFE642';
        case StatusPillValue.CRITICAL:
        case StatusPillValue.SIGNIFICANT:
        case StatusPillValue.LIKELY:
            return '#FFA229';
        case StatusPillValue.DISASTROUS:
        case StatusPillValue.HIGH:
        case StatusPillValue.MAJOR:
        case StatusPillValue.VERY_HIGH:
        case StatusPillValue.VERY_UNLIKELY:
            return '#F04A2D';
        default:
            return '';
    }
}

export function StatusPill({
    value,
    onlyContent,
    fallback,
    customColor,
    customLabel,
    tooltip,
}: StatusPillProps): ReactNode {
    const pillRef = useRef<HTMLSpanElement>(null);
    const isSupportedValue = Object.keys(StatusPillValue).includes(value);

    const label = useMemo(() => {
        if (customLabel) {
            return customLabel;
        }

        return value
            .replace('_', ' ')
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    }, [value, customLabel]);

    const backgroundColor = useMemo(() => {
        if (customColor) {
            return customColor;
        }

        return getStatusPillColor(value as StatusPillValue);
    }, [value, customColor]);

    if (!customColor && !isSupportedValue) {
        return fallback ?? null;
    }

    const content = (
        <div className="flex align-items-center">
            <div
                className="status-pill--indicator"
                style={{
                    backgroundColor,
                }}
            ></div>
            {label}
        </div>
    );

    const pillContent = onlyContent ? (
        content
    ) : (
        <Pill className="flex align-items-center">{content}</Pill>
    );

    if (!tooltip) {
        return pillContent;
    }

    return (
        <>
            <Tooltip target={pillRef} content={tooltip} position="top" />
            <span ref={pillRef} className="inline-flex">
                {pillContent}
            </span>
        </>
    );
}
